import { Button } from "@progress/kendo-react-buttons";
import {
  Field,
  Form,
  FormElement,
  FormRenderProps,
} from "@progress/kendo-react-form";
import { Loader } from "@progress/kendo-react-indicators";
import { Switch } from "@progress/kendo-react-inputs";
import { AxiosError } from "axios";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Else, If, Then } from "react-if";
import {
  AuthControllerService,
  TlxHttpResponse,
  UserControllerService,
} from "@/api";
import CopyToClipboard from "@/components/CopyToClipboard";
import { Modal } from "@/components/Dialog";
import { FormInput } from "@/components/Kendo/Form";
import { useUserState, useUserDispatch } from "@/context/UserContext";
import { getDefaultErrorMessage } from "@/helpers/error_message";
import useDialogState from "@/hooks/useDialogState";
import useMutation from "@/hooks/useMutation";
import useQuery from "@/hooks/useQuery";
import DisableIcon from "@/icons/disablemfa.svg?react";
import SuccessIcon from "@/icons/success.svg?react";
import { dateFormat } from "@/libs/date";
import { logOut } from "@/libs/store";
import "./mfa.scss";

const requiredItems = [1, 2, 3, 4, 5, 6];

// Kendo adds form keys in the order fields are first edited, so read the
// digits by position rather than by key order.
const toTotp = (formValues: Record<string, unknown>) =>
  requiredItems.map(item => formValues[`code-input-${item}`] ?? "").join("");

const MFAChoice: React.FC<{ onSuccess: () => void }> = ({ onSuccess }) => {
  const { data, isLoading } = useQuery({
    method: UserControllerService.getMfaQrCodeUsingPost,
  });

  const {
    mutate,
    isLoading: isMutating,
    error,
    isSuccess,
  } = useMutation(AuthControllerService.verifyTotpUsingPost);

  const { user } = useUserState();

  useEffect(() => {
    if (isSuccess) return onSuccess();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSuccess]);

  const onSubmit = useCallback(
    (totp: string) => {
      mutate({
        userRequest: {
          loginId: user?.userId,
          totp,
        },
      });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [user],
  );

  if (isLoading) {
    return <Loader size="medium" />;
  }

  return (
    <>
      <div className="tlx-mfa-container-choice">
        <div className="tlx-mfa-container-choice-content">
          <title>Scan the QR code</title>
          <div className="sub-text">
            Use an authenticator app or browser extension to scan.
          </div>
          <img
            height={150}
            src={`data:image/png;base64, ${data?.base64QrCodeImage}`}
          />
        </div>
        <div className="tlx-mfa-container-choice-or">or</div>
        <div className="tlx-mfa-container-choice-content">
          <title>Unable to scan?</title>
          <div className="sub-text">
            Use the setup key to manually configure your time based
            authenticator app.
            <div className="setup-code">
              <CopyToClipboard
                copyToText={data?.qrSetupKey}
                iconStyle={{ float: "right" }}
              >
                {data?.qrSetupKey}
              </CopyToClipboard>
            </div>
          </div>
        </div>
      </div>
      <div className="tlx-mfa-container-choice-footer">
        <div>Enter the code displayed on your authenticator app to verify</div>
        <MFAForm
          testId="mfa-setup"
          disabled={isMutating}
          onSubmit={onSubmit}
          error={
            isMutating ? undefined : (error as AxiosError<TlxHttpResponse>)
          }
          footer={formRenderProps => (
            <Button
              size="medium"
              themeColor="primary"
              type="submit"
              disabled={!formRenderProps.valid || isMutating}
            >
              Verify {isMutating && <Loader themeColor="light" />}
            </Button>
          )}
        />
      </div>
    </>
  );
};

export const MFAForm: React.FC<{
  testId: string;
  disabled?: boolean;
  /** Receives the six-digit code, assembled in input order. */
  onSubmit: (totp: string) => void;
  footer: (formRenderProps: FormRenderProps) => React.ReactNode;
  error?: AxiosError<TlxHttpResponse>;
}> = ({ testId, disabled = false, onSubmit, footer, error }) => {
  const { t } = useTranslation();
  const submitTimer = useRef<ReturnType<typeof setTimeout> | undefined>(
    undefined,
  );
  useEffect(() => () => clearTimeout(submitTimer.current), []);
  const validate = useCallback((values: Record<string, string>) => {
    const errors: Record<string, string> = {};
    requiredItems.map(item => {
      const name = `code-input-${item}`;
      const value = values[name];
      if (!/^\d$/.test(value ?? "")) {
        errors[name] = " ";
      }
    });
    return errors;
  }, []);

  const focusInput = (index: number) => {
    document.getElementById(`code-input-${index}`)?.focus();
  };

  const handleKeyDown = (
    e: React.KeyboardEvent<HTMLInputElement>,
    item: number,
    formRenderProps: FormRenderProps,
  ) => {
    if (e.key === "Backspace") {
      const currentValue = formRenderProps.valueGetter(`code-input-${item}`);
      if (!currentValue && item > 1) {
        e.preventDefault();
        focusInput(item - 1);
      }
    }
  };

  const handlePaste = (
    e: React.ClipboardEvent<HTMLInputElement>,
    formRenderProps: FormRenderProps,
  ) => {
    e.preventDefault();
    if (disabled) return;
    clearTimeout(submitTimer.current);
    const pasted = e.clipboardData.getData("text") ?? "";
    const digits = pasted.replace(/\D/g, "").slice(0, requiredItems.length);
    if (!digits) return;

    requiredItems.forEach((item, idx) => {
      formRenderProps.onChange(`code-input-${item}`, {
        value: digits[idx] ?? "",
      });
    });

    if (digits.length >= requiredItems.length) {
      focusInput(requiredItems.length);
      const form = e.currentTarget.form;
      if (form) {
        // Defer so Kendo Form state updates from onChange settle before submit.
        submitTimer.current = setTimeout(() => form.requestSubmit(), 0);
      }
    } else {
      focusInput(digits.length + 1);
    }
  };

  return (
    <Form
      validator={validate}
      onSubmit={formValues => {
        if (!disabled) onSubmit(toTotp(formValues));
      }}
      render={(formRenderProps: FormRenderProps) => (
        <FormElement>
          <div className="code-container">
            <div
              className="codes"
              role="group"
              aria-label={t("mfa.inputLabel")}
            >
              {requiredItems.map(item => (
                <Field
                  name={`code-input-${item}`}
                  key={`input-${item}`}
                  component={FormInput}
                  maxLength={1}
                  disabled={disabled}
                  aria-label={t("mfa.digitLabel", {
                    digit: item,
                    total: requiredItems.length,
                  })}
                  data-test-id={`${testId}-digit-${item}`}
                  inputMode="numeric"
                  pattern="[0-9]*"
                  autoComplete={item === 1 ? "one-time-code" : "off"}
                  onChange={({ value }) => {
                    if (Number.isInteger(parseInt(value))) {
                      focusInput(item + 1);
                    }
                  }}
                  onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) =>
                    handleKeyDown(e, item, formRenderProps)
                  }
                  onPaste={(e: React.ClipboardEvent<HTMLInputElement>) =>
                    handlePaste(e, formRenderProps)
                  }
                />
              ))}
            </div>
            <div className="code-footer">{footer(formRenderProps)}</div>
          </div>
          {error && (
            <div
              className="error"
              role="alert"
              style={{
                fontSize: "var(--tlx-font-size-12)",
                fontWeight: "500",
                marginTop: "var(--tlx-space-1)",
              }}
            >
              {(error?.response?.data as unknown as { data: string })?.data ||
                error?.response?.data?.message ||
                getDefaultErrorMessage(error)}
            </div>
          )}
        </FormElement>
      )}
    />
  );
};

const MFASuccess: React.FC<{ isOpen: boolean; onClose: () => void }> = ({
  isOpen,
  onClose,
}) => {
  return (
    <Modal
      title=""
      open={isOpen}
      onClose={onClose}
      closeIcon={true}
      hideFooter={true}
      width="500px"
    >
      <div className="tlx-mfa-container-success">
        <SuccessIcon />
        <div className="tlx-mfa-container-success-title">
          Multi-Factor Authentication has been successfully enabled.
        </div>
        <div className="tlx-mfa-container-success-subTitle">
          To ensure the strongest security, we recommend logging out of all your
          devices and signing in again.
        </div>
        <div className="tlx-mfa-container-success-action">
          <Button size="medium" themeColor="primary" onClick={logOut}>
            Logout from all sessions
          </Button>
        </div>
      </div>
    </Modal>
  );
};

const MFADisabled: React.FC<{
  isOpen: boolean;
  onClose: (isSuccess: boolean) => void;
}> = ({ isOpen, onClose: onCloseProps }) => {
  const { user } = useUserState();
  const userDispatch = useUserDispatch();

  const { mutate, isLoading, error, isSuccess } = useMutation(
    UserControllerService.disableMfaUsingPost,
  );

  const onSubmit = useCallback((totp: string) => {
    mutate({
      userRequest: {
        loginId: user?.userId,
        totp,
      },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (isSuccess) {
      userDispatch({ type: "DISABLED_MFA", data: null });
      onCloseProps(isSuccess);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSuccess]);

  const onClose = useCallback(() => {
    onCloseProps(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!isOpen) return null;
  return (
    <Modal
      title="  "
      open={isOpen}
      onClose={onClose}
      hideFooter={true}
      width="500px"
    >
      <div className="tlx-mfa-container-success">
        <DisableIcon />
        <div className="tlx-mfa-container-success-title">
          Disable Multi-Factor Authentication?
        </div>
        <div className="tlx-mfa-container-success-subTitle">
          By disabling you will no longer be able to use it to sign-in to your
          account.
        </div>
        <div className="tlx-mfa-container-success-subTitle">
          Enter TOTP code to continue
        </div>

        <MFAForm
          testId="mfa-disable"
          disabled={isLoading}
          onSubmit={onSubmit}
          footer={formRenderProps => (
            <>
              {error && (
                <div
                  className="error"
                  style={{
                    fontSize: "var(--tlx-font-size-12)",
                    fontWeight: "500",
                    margin: "var(--tlx-space-1) 0",
                    textAlign: "center",
                  }}
                >
                  {
                    (error as AxiosError<TlxHttpResponse>)?.response?.data
                      .message
                  }
                </div>
              )}
              <div className="tlx-mfa-container-success-action">
                <Button
                  size="medium"
                  themeColor="base"
                  onClick={onClose}
                  type="button"
                >
                  Cancel
                </Button>
                <Button
                  size="medium"
                  themeColor="secondary"
                  type="submit"
                  disabled={!formRenderProps.valid || isLoading}
                >
                  Disable
                  {isLoading && <Loader themeColor="light" />}
                </Button>
              </div>
            </>
          )}
        />
      </div>
    </Modal>
  );
};

const MFAEnabled: React.FC<{
  mfaEnableDate: string;
  isMfaEnabled: boolean;
}> = ({ mfaEnableDate, isMfaEnabled }) => {
  const [checked, setChecked] = React.useState<boolean>(isMfaEnabled);
  const [isDisabled, openDisabled, closeDisabled] = useDialogState();

  const { user } = useUserState();

  const onCloseDisabled = useCallback((isSuccess: boolean) => {
    if (!isSuccess) setChecked(true);
    closeDisabled();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      <div className="tlx-mfa-container-enabled">
        <div>
          <div className="tlx-mfa-container-enabled-title">
            Trustlogix authentication{" "}
            <span className="tlx-mfa-container-enabled-synced">Synced</span>
          </div>
          <div className="tlx-mfa-container-enabled-description">
            Added on {dateFormat(mfaEnableDate, "LLL dd yyy")}
          </div>
        </div>
        <div className="actions">
          <div>
            <Switch
              offLabel=""
              onLabel=""
              size="small"
              checked={checked}
              disabled={user?.mfaEnabled === false}
              onChange={() => {
                setChecked(!checked);
                openDisabled();
              }}
            />
          </div>
        </div>
      </div>

      <MFADisabled isOpen={isDisabled} onClose={onCloseDisabled} />
    </>
  );
};

const MFA: React.FC<{
  isOpen: boolean;
  onClose: () => void;
  isMfaEnabled: boolean;
  mfaEnableDate?: string;
}> = ({ isOpen, isMfaEnabled, onClose, mfaEnableDate = "" }) => {
  const [isSelected, setIsSelected] = useState(false);
  const [isSuccess, setSuccess] = useState(false);
  const onSuccessClose = useCallback(() => {
    setSuccess(false);
    onClose();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (isSuccess) {
    return <MFASuccess isOpen={true} onClose={onSuccessClose} />;
  }

  return (
    <Modal
      title="Multi-Factor Authentication"
      open={isOpen}
      onClose={onClose}
      closeIcon={true}
      hideFooter={true}
      width="1050px"
    >
      <div className="tlx-mfa-container">
        <If condition={isMfaEnabled}>
          <Then>
            <MFAEnabled
              mfaEnableDate={mfaEnableDate}
              isMfaEnabled={isMfaEnabled}
            />
          </Then>
          <Else>
            <If condition={isSelected}>
              <Then>
                <MFAChoice onSuccess={() => setSuccess(true)} />
              </Then>
              <Else>
                <div className="tlx-mfa-container-main">
                  <title>Authenticator app </title>
                  <div className="tlx-mfa-container-main-info">
                    <div>
                      First download Google Authenticator from the{" "}
                      <a
                        className="k-link"
                        target="_blank"
                        href="https://play.google.com/store/apps/details?id=com.google.android.apps.authenticator2"
                        rel="noreferrer"
                      >
                        Google play store{" "}
                      </a>
                      , and{" "}
                      <a
                        className="k-link"
                        target="_blank"
                        href="https://apps.apple.com/us/app/google-authenticator/id388497605"
                        rel="noreferrer"
                      >
                        IOS App Store{" "}
                      </a>
                    </div>
                    <div>
                      <Button
                        size="medium"
                        icon="plus"
                        fillMode="outline"
                        onClick={() => setIsSelected(true)}
                      >
                        Set up authenticator
                      </Button>
                    </div>
                  </div>
                </div>
              </Else>
            </If>
          </Else>
        </If>
      </div>
    </Modal>
  );
};

export default MFA;
